/**
 * DashboardKpiInfoPopover
 *
 * Bulle d'information enrichie pour un KPI du dashboard pilotage.
 * Lit la définition depuis DASHBOARD_KPI_REGISTRY et affiche :
 * - description métier
 * - formule en langage naturel
 * - ce qui est inclus / exclu
 * - période
 * - hypothèses éventuelles
 * - décomposition détaillée si extraData est fournie (ex: explicabilité ETP 2027)
 *
 * Usage :
 *   <DashboardKpiInfoPopover kpiKey="ca_annuel_net" />
 *   <DashboardKpiInfoPopover kpiKey="etp_factures_annuels" extraData={{ year: 2027, ... }} />
 */

import { Info } from "lucide-react";
import { getDashboardKpi } from "@/admin/config/dashboardKpiDefinitions.ts";
import { formatPilotageDecimal, formatPilotageCount } from "@/lib/pilotageUi";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const PERIOD_LABELS = {
  monthly: "Mensuel",
  annual: "Annuel",
  instant: "Instantané",
};

const formatDays = (val) => {
  if (val == null) return "—";
  const num = Number(val);
  if (!Number.isFinite(num)) return "—";
  return Number.isInteger(num) ? formatPilotageCount(num) : formatPilotageDecimal(num);
};

const DashboardKpiInfoPopover = ({ kpiKey, extraData }) => {
  const kpi = getDashboardKpi(kpiKey);
  if (!kpi) return null;

  const hasEtpBreakdown = kpiKey === "etp_factures_annuels" && extraData;
  const annualBaseDays = extraData?.annualBaseDays || 210;
  const annualBilledDays = extraData?.annualBilledDays ?? (
    extraData?.annualBilledEtp ? Math.round(extraData.annualBilledEtp * annualBaseDays * 100) / 100 : null
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-600/50 text-slate-400 transition-colors hover:border-lenvia-blue/60 hover:text-lenvia-blue focus:outline-none focus:ring-2 focus:ring-lenvia-blue/30 focus:ring-offset-0"
          aria-label={`Informations sur ${kpi.label}`}
          data-testid={`kpi-info-trigger-${kpiKey}`}
        >
          <Info className="h-3 w-3" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={`${hasEtpBreakdown ? "w-96" : "w-80"} border border-white/10 bg-slate-900 p-0 shadow-xl shadow-black/40`}
        align="start"
        sideOffset={6}
        data-testid={`kpi-info-content-${kpiKey}`}
      >
        <div className="space-y-3 p-4">

          {/* En-tête : label + badge période */}
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-white leading-snug">
              {hasEtpBreakdown && extraData.isForecast ? "ETP facturables prévisionnels" : kpi.label}
              {hasEtpBreakdown && extraData.year ? ` — ${extraData.year}` : ""}
            </p>
            <span className="flex-none rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {PERIOD_LABELS[kpi.period] ?? kpi.period}
            </span>
          </div>

          {/* Section Explicabilité ETP si disponible */}
          {hasEtpBreakdown && (
            <div className="rounded-lg border border-lenvia-blue/20 bg-lenvia-blue/5 p-3 space-y-2.5" data-testid="etp-explainability-card">
              <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-300">
                <div className="bg-slate-900/60 p-2 rounded border border-white/5">
                  <span className="text-slate-400 block text-[10px]">Jours facturables</span>
                  <span className="font-semibold text-white" data-testid="etp-modal-days">{annualBilledDays !== null ? `${formatDays(annualBilledDays)} j` : "—"}</span>
                </div>
                <div className="bg-slate-900/60 p-2 rounded border border-white/5">
                  <span className="text-slate-400 block text-[10px]">Base annuelle</span>
                  <span className="font-semibold text-white" data-testid="etp-modal-base">{annualBaseDays} j</span>
                </div>
                <div className="bg-slate-900/60 p-2 rounded border border-white/5">
                  <span className="text-slate-400 block text-[10px]">ETP calculés</span>
                  <span className="font-semibold text-emerald-400" data-testid="etp-modal-etp">{extraData.annualBilledEtp !== null ? `${formatPilotageDecimal(extraData.annualBilledEtp)} ETP` : "—"}</span>
                </div>
              </div>

              {Array.isArray(extraData.rows) && extraData.rows.length > 0 && (
                <div className="pt-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                    Détail par consultant / mission ({extraData.rows.length})
                  </p>
                  <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 text-[11px] divide-y divide-white/5" data-testid="etp-breakdown-list">
                    {extraData.rows.map((row, idx) => {
                      const billable = Number(row.billableDays || 0);
                      const contrib = row.etpContribution != null
                        ? Number(row.etpContribution)
                        : (billable / annualBaseDays);
                      return (
                        <div key={row.missionId || idx} className="pt-1.5 first:pt-0 flex items-start justify-between gap-2" data-testid={`etp-row-${row.missionId || idx}`}>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-200 truncate">{row.consultantLabel}</p>
                            <p className="text-[10px] text-slate-400 truncate">{row.missionLabel || row.clientName || "Mission"}</p>
                            {(row.startDate || row.endDate) && (
                              <p className="text-[9px] text-slate-500">
                                {row.startDate || "Début"} → {row.endDate || "En cours"}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex-none">
                            <span className="font-semibold text-white">{formatPilotageDecimal(contrib)} ETP</span>
                            <p className="text-[10px] text-slate-400">
                              {formatDays(billable)} j
                              {row.freeDays > 0 ? ` (${formatDays(row.freeDays)}j grat.)` : ""}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <p className="text-xs leading-relaxed text-slate-300">
            {kpi.description}
          </p>

          {/* Formule */}
          <div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Formule
            </p>
            <p className="mt-1 text-xs leading-relaxed text-lenvia-blue/90 font-medium">
              {kpi.formulaText}
            </p>
          </div>

          {/* Inclus */}
          {Array.isArray(kpi.included) && kpi.included.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Inclut
              </p>
              <ul className="mt-1.5 space-y-1">
                {kpi.included.map((item, i) => (
                  <li
                    key={`included-${i}`}
                    className="flex items-start gap-1.5 text-xs text-slate-300 leading-snug"
                  >
                    <span
                      className="mt-[3px] h-1.5 w-1.5 flex-none rounded-full bg-emerald-400/70"
                      aria-hidden="true"
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Exclus */}
          {Array.isArray(kpi.excluded) && kpi.excluded.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Exclut
              </p>
              <ul className="mt-1.5 space-y-1">
                {kpi.excluded.map((item, i) => (
                  <li
                    key={`excluded-${i}`}
                    className="flex items-start gap-1.5 text-xs text-slate-300 leading-snug"
                  >
                    <span
                      className="mt-[3px] h-1.5 w-1.5 flex-none rounded-full bg-rose-400/70"
                      aria-hidden="true"
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Hypothèses */}
          {Array.isArray(kpi.assumptions) && kpi.assumptions.length > 0 && (
            <div className="rounded-lg border border-amber-400/20 bg-amber-500/5 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400/80">
                Hypothèses
              </p>
              <ul className="mt-1.5 space-y-1">
                {kpi.assumptions.map((item, i) => (
                  <li
                    key={`assumption-${i}`}
                    className="text-xs leading-relaxed text-amber-200/80"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DashboardKpiInfoPopover;
